/**
 * Pure model for the customer session (lesson/tour) slot picker — payload
 * types for /api/bookings/session-availability plus display helpers.
 *
 * Timezone contract: localDate/startTime are LOCATION-LOCAL wall-clock strings
 * from the API. Format them with pure string math only — converting through
 * Date would re-render them in the browser's timezone and show wrong times to
 * a traveler booking from another zone. startsAt is the canonical UTC
 * occurrence instant, echoed verbatim to session-checkout (never displayed).
 */

// eligibleLadderEntries/eligiblePartySizes moved to @sifly/shared-utils
// (2026-08-01) when the portal's manual-booking modal gained the same need —
// re-exported here so this module's existing import path keeps working.
export { eligibleLadderEntries, eligiblePartySizes } from '@sifly/shared-utils'

/** One calendar day from the month-mode availability response. */
export type SessionMonthDay = { date: string; openSlots: number; totalSlots: number }

/** One bookable occurrence from the day-mode availability response. */
export type SessionSlot = {
  startsAt: string // ISO UTC instant — the hold key
  localDate: string // YYYY-MM-DD, location-local
  startTime: string // HH:MM, location-local
  remaining: number
}

/** '14:05' → '2:05 PM' (pure string math — no Date, no timezone). */
export function formatSlotTime(startTime: string): string {
  const [hStr, mStr] = startTime.split(':')
  const h = Number(hStr)
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${mStr} ${suffix}`
}

/** '2026-07-11' → 'Sat, Jul 11' (UTC-pinned so the string never shifts a day). */
export function formatLocalDateLabel(localDate: string): string {
  return new Date(localDate + 'T00:00:00Z').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}

/** 90 → '1.5 hours', 60 → '1 hour', 45 → '45 minutes'. */
export function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`
  const hours = minutes / 60
  const rounded = Math.round(hours * 10) / 10
  return `${rounded} hour${rounded === 1 ? '' : 's'}`
}

/** cutoff_minutes → customer-facing lead-time note, or null when none. */
export function cutoffLabel(cutoffMinutes: number): string | null {
  if (cutoffMinutes <= 0) return null
  if (cutoffMinutes < 60) return `Book at least ${cutoffMinutes} minutes ahead`
  const hours = Math.round((cutoffMinutes / 60) * 10) / 10
  return `Book at least ${hours} hour${hours === 1 ? '' : 's'} ahead`
}
